import React, { FC, useContext, useMemo, useState, useEffect } from 'react';
import styles from './index.module.scss';
import { EchartsWithTheme, Empty, useTheme, DatePicker, Tooltip } from '@maxtropy/components';
import { Progress, Spin } from 'antd';
import { useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { DateContext, ShareDataWrapperContext } from '../../../../index';
import { IndexTimeActive } from '../../../SwitchDate';
import {
  apiV2PaintingDetailWorkshopOverviewPost,
  apiV2PaintingDetailCrossAreaPost,
} from '@maxtropy/device-customer-apis-v2';
import { keepOneAndWanOrLine, keepTwoOrLine } from '@/utils/utils';
import iconElecCircle from '../../../../imgs/icon_elec_circle.png';
import iconCompressedCircle from '../../../../imgs/icon_compressed_circle.png';
import iconGasCircle from '../../../../imgs/icon_gas_circle.png';
import iconSpinCircle from '../../../../imgs/icon_spin_circle.png';
// 分跨间用能情况使用的图标
import iconCompressedAir from '../../../../imgs/icon_electricity_workshop.png';
import iconNaturalGas from '../../../../imgs/icon_natural_gas_workshop.png';
import iconEnergyCost from '../../../../imgs/icon_energy_total.png';

interface WorkshopDetailModalProps {
  workshopCode: number;
}

const ELE_COLOR = '#52E7FF';
const AIR_COLOR = '#2D8DFF';
const GAS_COLOR = '#FFCB47';

const ENERGY_ICONS: Record<string, string> = {
  electricity: iconElecCircle,
  compressedAir: iconCompressedCircle,
  naturalGas: iconGasCircle,
  cost: iconSpinCircle,
};

const createTrendOption = (
  data: { date: string; electricity?: number; compressedAir?: number; naturalGas?: number }[],
  theme: string
) => {
  const colors = [ELE_COLOR, AIR_COLOR, GAS_COLOR];
  return {
    backgroundColor: 'transparent',
    color: colors,
    legend: [
      {
        top: 0,
        right: 170,
        icon: 'rect',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 20,
        data: ['电'],
      },
      {
        top: 0,
        right: 0,
        icon: 'rect',
        itemWidth: 16,
        itemHeight: 4,
        itemGap: 20,
        data: ['压缩空气', '天然气'],
      },
    ],
    tooltip: {
      trigger: 'axis' as const,
      confine: true,
      extraCssText: 'width: 220px;',
      formatter: (items: any) => {
        const { axisValue } = items[0];
        let str = `<div style="padding: 4px;">${axisValue}</div>`;
        items.forEach((item: any) => {
          const unit = item.seriesName === '电' ? 'kWh' : 'm³';
          str += `<div style="margin-top:8px;"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${
            item.marker
          }</span>${item.seriesName}</div><div>${keepOneAndWanOrLine(item.value)} ${unit}</div></div></div>`;
        });
        return str;
      },
    },
    grid: { bottom: 5 },
    xAxis: {
      type: 'category' as const,
      data: data.map(d => d.date),
    },
    yAxis: [
      { type: 'value' as const, name: 'kWh', splitNumber: 5, axisTick: { alignTicks: true } },
      { type: 'value' as const, name: 'm³', splitNumber: 5, axisTick: { alignTicks: true } },
    ],
    series: [
      {
        name: '电',
        type: 'bar' as const,
        barMaxWidth: 20,
        data: data.map(d => d.electricity),
        itemStyle: { color: colors[0] },
      },
      {
        name: '压缩空气',
        type: 'line' as const,
        smooth: true,
        symbol: 'none',
        yAxisIndex: 1,
        data: data.map(d => d.compressedAir),
        itemStyle: { color: colors[1] },
      },
      {
        name: '天然气',
        type: 'line' as const,
        smooth: true,
        symbol: 'none',
        yAxisIndex: 1,
        data: data.map(d => d.naturalGas),
        itemStyle: { color: colors[2] },
      },
    ],
  };
};

const WorkshopDetailModal: FC<WorkshopDetailModalProps> = ({ workshopCode }) => {
  const theme = useTheme();
  const { activeKey } = useContext(DateContext);

  // 弹窗维护自己的时间状态，不影响外部页面
  const { selectedDate: externalSelectedDate } = useContext(DateContext);
  const [localSelectedDate, setLocalSelectedDate] = useState<Dayjs>(externalSelectedDate);

  // 弹窗打开时同步外部时间（Modal 有 destroyOnClose，每次打开会重新创建）
  useEffect(() => {
    setLocalSelectedDate(externalSelectedDate);
  }, [externalSelectedDate]);

  // 请求参数（使用本地时间状态）
  const requestParams = useMemo(() => {
    const unit = activeKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year';
    const startTime = localSelectedDate.startOf(unit).valueOf();
    const now = dayjs();
    const isCurrentPeriod =
      localSelectedDate.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY') ===
      now.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY');
    const endTime = isCurrentPeriod
      ? now.subtract(1, 'day').endOf('day').valueOf()
      : localSelectedDate.endOf(unit).valueOf();
    return { startTime, endTime, workshop: workshopCode };
  }, [activeKey, localSelectedDate, workshopCode]);

  // 调用 workshop-overview 接口
  const { data: overviewData, loading: overviewLoading } = useRequest(
    () => apiV2PaintingDetailWorkshopOverviewPost(requestParams),
    { refreshDeps: [requestParams] }
  );

  // 调用 cross-area 接口
  const { data: crossAreaData, loading: crossAreaLoading } = useRequest(
    () => apiV2PaintingDetailCrossAreaPost(requestParams),
    { refreshDeps: [requestParams] }
  );

  const loading = overviewLoading || crossAreaLoading;

  // 指标数据
  const indicators = overviewData?.indicators;
  const totalCost = overviewData?.totalCost;
  const costRatios = overviewData?.costRatios;

  // 统计卡片数据
  const statsCards = [
    {
      key: 'electricity',
      label: '用电量',
      unit: 'kWh',
      value: indicators?.electricity?.actual,
      plan: indicators?.electricity?.plan,
      rate: keepTwoOrLine(indicators?.electricity?.completionRate),
    },
    {
      key: 'compressedAir',
      label: '压缩空气',
      unit: 'm³',
      value: indicators?.air?.actual,
      plan: indicators?.air?.plan,
      rate: keepTwoOrLine(indicators?.air?.completionRate),
    },
    {
      key: 'naturalGas',
      label: '天然气',
      unit: 'm³',
      value: indicators?.gas?.actual,
      plan: indicators?.gas?.plan,
      rate: keepTwoOrLine(indicators?.gas?.completionRate),
    },
    {
      key: 'cost',
      label: '能耗成本',
      unit: '元',
      value: totalCost?.actual,
      plan: totalCost?.plan,
      rate: keepTwoOrLine(totalCost?.completionRate),
    },
  ];

  // 趋势数据
  const trendData = useMemo(() => {
    return (
      overviewData?.dailyTrend?.map(t => ({
        date: dayjs(t.timestamp).format('MM-DD'),
        electricity: t.electricity,
        compressedAir: t.air,
        naturalGas: t.gas,
      })) || []
    );
  }, [overviewData]);

  const trendOption = useMemo(() => createTrendOption(trendData, theme), [trendData, theme]);

  // 跨区数据
  const spanData = useMemo(() => {
    return (
      crossAreaData?.zones?.map(z => ({
        name: z.zoneCode ?? '--',
        electricity: z.electricity,
        compressedAir: z.air,
        naturalGas: z.gas,
        cost: z.cost,
      })) || []
    );
  }, [crossAreaData]);

  // 成本明细
  const costBreakdown = useMemo(() => {
    return (
      costRatios?.map(c => ({
        name: c.energyType === 'ELECTRICITY' ? '用电成本' : c.energyType === 'AIR' ? '压缩空气成本' : '天然气成本',
        value: c.cost,
        ratio: keepTwoOrLine(c.ratio),
        color: c.energyType === 'ELECTRICITY' ? ELE_COLOR : c.energyType === 'AIR' ? AIR_COLOR : GAS_COLOR,
      })) || []
    );
  }, [costRatios]);

  if (loading) {
    return (
      <div className={styles.loading_wrapper}>
        <Spin />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Date Selector */}
      <div className={styles.dateSelector}>
        <DatePicker
          picker={activeKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year'}
          value={localSelectedDate}
          onChange={date => date && setLocalSelectedDate(date)}
          allowClear={false}
          className={styles.datePicker}
        />
      </div>

      {/* Stats Row */}
      <div className={styles.statsRow}>
        {statsCards.map(card => (
          <div key={card.key} className={styles.statsCard}>
            <div className={styles.statsCardTop}>
              <div className={styles.statsCardIconWrapper}>
                {card.key === 'cost' ? (
                  <div className={styles.iconStack}>
                    <img className={styles.statsCardIconSpin} src={iconSpinCircle} alt="旋转背景" />
                    <img className={styles.iconStackInner} src={iconEnergyCost} alt={card.label} />
                  </div>
                ) : (
                  <img className={styles.statsCardIcon} src={ENERGY_ICONS[card.key]} alt={card.label} />
                )}
              </div>
              <div className={styles.statsCardData}>
                <div className={styles.statsCardValueRow}>
                  <span className={styles.statsCardLabel}>
                    {card.label}({card.unit}):
                  </span>
                  <span className={styles.statsCardValue}>{keepOneAndWanOrLine(card.value)}</span>
                </div>
                <div className={styles.statsCardDivider} />
                <div className={styles.statsCardPlanRow}>
                  <span className={styles.statsCardPlanLabel}>计划用量({card.unit}):</span>
                  <span className={styles.statsCardPlanValue}>{keepOneAndWanOrLine(card.plan)}</span>
                </div>
              </div>
            </div>
            <div className={styles.statsCardRate}>
              <span className={styles.statsCardRateLabel}>完成率:</span>
              <Progress
                percent={Number(card.rate) || 0}
                showInfo={false}
                strokeColor={{ from: '#2D8DFF', to: '#52E7FF' }}
                trailColor="rgba(45, 141, 255, 0.2)"
                size={[100, 6]}
              />
              <span className={styles.statsCardRateText}>{card.rate}%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Span Data Section */}
      <div className={styles.sectionTitle}>
        <span className={styles.sectionTitleText}>分跨间用能情况</span>
      </div>
      <div className={styles.spanGrid}>
        {spanData.map(span => (
          <div key={span.name} className={styles.spanCard}>
            <div className={styles.spanCardTitle}>{span.name}</div>
            <div className={styles.spanCardBody}>
              <div className={styles.spanRow}>
                <img className={styles.spanIcon} src={iconElecCircle} alt="电" />
                <div className={styles.spanInfo}>
                  <Tooltip title="用电量(kWh)">
                    <span className={styles.spanLabel}>用电量(kWh)</span>
                  </Tooltip>
                  <span className={styles.spanValue}>{keepOneAndWanOrLine(span.electricity)}</span>
                </div>
              </div>
              <div className={styles.spanRow}>
                <img className={styles.spanIcon} src={iconCompressedAir} alt="压缩空气" />
                <div className={styles.spanInfo}>
                  <Tooltip title="压缩空气(m³)">
                    <span className={styles.spanLabel}>压缩空气(m³)</span>
                  </Tooltip>
                  <span className={styles.spanValue}>{keepOneAndWanOrLine(span.compressedAir)}</span>
                </div>
              </div>
              <div className={styles.spanRow}>
                <img className={styles.spanIcon} src={iconNaturalGas} alt="天然气" />
                <div className={styles.spanInfo}>
                  <Tooltip title="天然气(m³)">
                    <span className={styles.spanLabel}>天然气(m³)</span>
                  </Tooltip>
                  <span className={styles.spanValue}>{keepOneAndWanOrLine(span.naturalGas)}</span>
                </div>
              </div>
              <div className={styles.spanRow}>
                <img className={styles.spanIcon} src={iconEnergyCost} alt="能耗成本" />
                <div className={styles.spanInfo}>
                  <Tooltip title="能耗成本(元)">
                    <span className={styles.spanLabel}>能耗成本(元)</span>
                  </Tooltip>
                  <span className={styles.spanValueCost}>{keepOneAndWanOrLine(span.cost)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom Section */}
      <div className={styles.bottomSection}>
        {/* Left: Trend Chart */}
        <div className={styles.bottomLeft}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionTitleText}>用能趋势</span>
          </div>
          <div className={styles.trendChartWrapper}>
            <EchartsWithTheme
              theme={theme}
              type="bar"
              style={{ width: '100%', height: '100%' }}
              option={trendOption}
              empty={{ isShowEmpty: !trendData?.length, emptyNode: <Empty /> }}
            />
          </div>
        </div>

        {/* Right: Cost Breakdown */}
        <div className={styles.bottomRight}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionTitleText}>本期能耗成本明细</span>
          </div>
          <div className={styles.costBreakdown}>
            {costBreakdown.map(item => (
              <div key={item.name} className={styles.costRow}>
                <img
                  className={styles.costIcon}
                  src={
                    ENERGY_ICONS[
                      item.name === '用电成本'
                        ? 'electricity'
                        : item.name === '天然气成本'
                        ? 'naturalGas'
                        : 'compressedAir'
                    ]
                  }
                  alt={item.name}
                />
                <div className={styles.costInfo}>
                  <Tooltip title={item.name}>
                    <span className={styles.costName}>{item.name}</span>
                  </Tooltip>
                  <Tooltip title={`${keepOneAndWanOrLine(item.value)}元`}>
                    <span className={styles.costValue}>{keepOneAndWanOrLine(item.value)}元</span>
                  </Tooltip>
                </div>
                <div className={styles.costProgress}>
                  <Progress
                    percent={Number(item.ratio) || 0}
                    showInfo={false}
                    strokeColor={item.color}
                    trailColor="rgba(255,255,255,0.1)"
                    size={[100, 8]}
                  />
                  <span className={styles.costRatio}>{item.ratio}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkshopDetailModal;
